
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Team, User, Track, LeagueSettings, RuleGroup, RuleItem, Season } from '../types';
import { apiRequest } from '../utils/api';
import { AlertTriangle } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { useAuth } from './AuthContext';

interface LeagueContextType {
    teams: Team[];
    drivers: User[]; 
    tracks: Track[];
    seasons: Season[];
    settings: LeagueSettings;
    rules: RuleGroup[];
    teamThemeEnabled: boolean; // NEW
    toggleTeamTheme: () => void; // NEW
    updateTeam: (updatedTeam: Team) => Promise<void>;
    addTeam: (newTeam: Team) => Promise<void>;
    deleteTeam: (teamId: string) => Promise<void>;
    refreshData: () => Promise<void>;
    refreshSettings: () => Promise<void>;
    isLoading: boolean;
    error: string | null;
}

const LeagueContext = createContext<LeagueContextType | undefined>(undefined);

export const LeagueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, isLoading: authLoading } = useAuth();
    const [teams, setTeams] = useState<Team[]>([]);
    const [drivers, setDrivers] = useState<User[]>([]);
    const [tracks, setTracks] = useState<Track[]>([]);
    const [seasons, setSeasons] = useState<Season[]>([]);
    const [rules, setRules] = useState<RuleGroup[]>([]);
    
    // NEW: Team Theme State
    const [teamThemeEnabled, setTeamThemeEnabled] = useState<boolean>(() => {
        return localStorage.getItem('sdc_use_team_theme') === 'true';
    });

    const [settings, setSettings] = useState<LeagueSettings>({
        point_fastest_lap: 1,
        point_dotd: 0,
        protest_deadline_hours: 24,
        penalty_points_ban: 12,
        upload_limit_mb: 50,
        dotd_window_hours: 72,
        attendance_strikes_limit: 5,
        theme_color: '#e10600',
        league_name: 'Sunday Driver Cup',
        league_logo_url: null,
        reserve_logo_url: null,
        twitch_channel: null,
        discord_webhook_url: null,
        news_webhook_url: null,
        points_50: [],
        points_100: [],
        points_sprint: [],
        dotd_badge_id: null,
        penalties_apply_to_team: false
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const toggleTeamTheme = () => {
        setTeamThemeEnabled(prev => {
            const newVal = !prev;
            localStorage.setItem('sdc_use_team_theme', String(newVal));
            // Force reload if disabling to reset colors immediately handled in Layout
            if (!newVal) {
                 document.documentElement.style.setProperty('--f1-red', settings.theme_color);
            }
            return newVal;
        });
    };

    const fetchSettings = async () => {
        try {
            const res = await apiRequest<LeagueSettings>('settings.php');
            if (res) {
                setSettings(res);
                // Apply default theme if team theme is disabled
                if (res.theme_color && localStorage.getItem('sdc_use_team_theme') !== 'true') {
                    document.documentElement.style.setProperty('--f1-red', res.theme_color);
                }
                if (res.league_name) {
                    document.title = res.league_name;
                }
            }
        } catch (e) {
            console.warn("Could not load settings", e);
        }
    };

    const fetchData = async () => {
        try {
            setError(null);
            await fetchSettings();
            if (!user) {
                setTeams([]);
                setDrivers([]);
                setTracks([]);
                setSeasons([]);
                setRules([]);
                return;
            }
            
            const [teamsData, usersData, tracksData, seasonsData, rulesRaw] = await Promise.all([
                apiRequest<Team[]>('teams.php'),
                apiRequest<User[]>('users.php'),
                apiRequest<Track[]>('tracks.php'),
                apiRequest<Season[]>('seasons.php?all=true'),
                apiRequest<any[]>('rules.php')
            ]);
            setTeams(teamsData);
            setDrivers(usersData);
            setTracks(tracksData || []);
            setSeasons(seasonsData || []);

            if (Array.isArray(rulesRaw)) {
                const groups: Record<string, RuleItem[]> = {};
                rulesRaw.forEach(r => {
                    if (!groups[r.group_name]) groups[r.group_name] = [];
                    groups[r.group_name].push({
                        db_id: r.id,
                        id: r.rule_key,
                        label: r.description,
                        points: r.points,
                        extra: r.extra
                    });
                });
                
                const groupedRules: RuleGroup[] = Object.keys(groups).map(g => ({
                    group: g,
                    items: groups[g]
                }));
                setRules(groupedRules);
            }

        } catch (error: any) {
            console.error("Failed to fetch league data", error);
            setError(error.message || "Database connection failed. Please check db.php configuration.");
        }
    };

    useEffect(() => {
        if (authLoading) return;
        const init = async () => {
            setIsLoading(true);
            await fetchData();
            setIsLoading(false);
        };
        init();
    }, [user, authLoading]);

    const refreshData = async () => {
        setIsLoading(true);
        await fetchData();
        setIsLoading(false);
    };

    const updateTeam = async (updatedTeam: Team) => {
        try {
            await apiRequest('teams.php', 'PUT', updatedTeam);
            setTeams(prev => prev.map(t => t.id === updatedTeam.id ? updatedTeam : t));
        } catch (e) {
            alert("Error updating team");
        }
    };

    const addTeam = async (newTeam: Team) => {
        try {
            const res = await apiRequest<{id: string}>('teams.php', 'POST', newTeam);
            setTeams(prev => [...prev, { ...newTeam, id: res.id }]);
        } catch (e) {
            alert("Error adding team");
        }
    };

    const deleteTeam = async (teamId: string) => {
        try {
            await apiRequest(`teams.php?id=${teamId}`, 'DELETE');
            setTeams(prev => prev.filter(t => t.id !== teamId));
        } catch (e) {
            alert("Error deleting team");
        }
    };

    if (error) {
        return (
            <div className="min-h-screen bg-f1-dark flex items-center justify-center p-4">
                <div className="bg-red-900/20 border border-red-600 p-8 rounded-lg max-w-md text-center shadow-2xl">
                    <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-white mb-2 uppercase">Connection Error</h2>
                    <p className="text-red-200 mb-6">{error}</p>
                    <p className="text-sm text-gray-400">
                        Check your <strong>db.php</strong> file on the server. <br/>
                        Ensure database credentials (User, Password, Host) are correct.
                    </p>
                    <button 
                        onClick={() => window.location.reload()}
                        className="mt-6 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded transition-colors"
                    >
                        Retry Connection
                    </button>
                </div>
            </div>
        );
    }

    return (
        <LeagueContext.Provider value={{ 
            teams, drivers, tracks, seasons, settings, rules, 
            teamThemeEnabled, toggleTeamTheme, // Export new values
            updateTeam, addTeam, deleteTeam, 
            refreshData, refreshSettings: fetchSettings, 
            isLoading, error 
        }}>
            {children}
        </LeagueContext.Provider>
    );
};

export const useLeague = () => {
    const context = useContext(LeagueContext);
    if (!context) {
        throw new Error('useLeague must be used within a LeagueProvider');
    }
    return context;
};
